import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { UVChart } from '../charts';
import {
  enrichedSeriesToTimeSeries,
  formatUvIndexHeaderParts,
  getClosestEnrichedPoint,
  getForecastDayDomain,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { useGraphTime } from './GraphTimeContext';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from './GraphCardHeader';
import GraphCardNoData from './GraphCardNoData';

export interface UVCardProps {
  uvPoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  isToday: boolean;
  latitude: number;
  longitude: number;
}

const UVCard: FC<UVCardProps> = ({
  uvPoints,
  selectedDate,
  timeZone,
  isToday,
  latitude,
  longitude,
}) => {
  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('uv');

  const uvSeries = useMemo(
    () => graphPointsToEnrichedSeries(uvPoints, selectedDate, timeZone),
    [uvPoints, selectedDate, timeZone],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(uvSeries, domain),
    [domain, uvSeries],
  );

  const selectedUvPoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(uvSeries, selectedChartTime);
  }, [uvSeries, selectedChartTime]);

  const uvHeaderParts = formatUvIndexHeaderParts(selectedUvPoint?.value);

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="UV Index"
        testId="weather-forecast-uv-card"
        analyticsCard="uv"
      >
        <GraphCardNoData
          chartLabel="UV Index"
          stationType="general"
          isLocationMissingData={uvPoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="UV Index"
      testId="weather-forecast-uv-card"
      analyticsCard="uv"
    >
      <GraphCardHeader
        primary={uvHeaderParts?.primary ?? '—'}
        secondary={uvHeaderParts?.secondary}
      />

      <Box width="100%">
        <UVChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>
    </CollapsibleCard>
  );
};

export default UVCard;
